import React, { useState, useEffect } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { PMField } from "../../../utils/PMField";
import { PMButton } from "../../../utils/PMButton";
import { TextField } from "@mui/material";
import ModernProductSelector from "../../../utils/Selector/ModernProductSelector";
import { fetchProducts } from "../../../../api/product";
import { XMarkIcon } from "@heroicons/react/24/outline";

const formatPriceWithTwoDecimals = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const stringValue = String(value).trim();
  if (stringValue === "") return "";
  if (stringValue.includes(",")) {
    const parts = stringValue.split(",");
    const integerPart = parts[0] || "";
    const decimalPart = parts[1] || "";
    let formattedDecimal = decimalPart.substring(0, 2);
    if (formattedDecimal.length === 1) {
      formattedDecimal = formattedDecimal + "0";
    } else if (formattedDecimal.length === 0) {
      formattedDecimal = "00";
    }
    return `${integerPart},${formattedDecimal}`;
  }
  if (/^\d+$/.test(stringValue)) {
    return `${stringValue},00`;
  }
  return stringValue;
};

const parseNumericValue = (value) => {
  if (!value || value === "") return 0;
  const stringValue = String(value);
  const normalized = stringValue.replace(/,/g, ".");
  return parseFloat(normalized) || 0;
};

/**
 * Redigering av rapportvarelinje (produkt, antall, priser).
 * Kan brukes inne i Rediger vareforbruk eller som egen dialog.
 */
export const ReportItemLineEditorForm = ({
  sourceItem,
  /** Når false synkroniseres ikke felter (dialog lukket) */
  active = true,
  onSave,
  submitLabel = "Lagre",
  showFooterCancel = true,
  onCancel,
  /** Etter lagring uten å vise toast her (f.eks. foreldre viser melding) */
  skipSuccessToast = false,
  /** Vises under knapperekken (f.eks. slett-seksjon) */
  childrenAfterFooter,
}) => {
  const [productSelectorOpen, setProductSelectorOpen] = useState(false);
  const [articleNumber, setArticleNumber] = useState("");
  const [currentItem, setCurrentItem] = useState(sourceItem);
  const [purchasePriceRaw, setPurchasePriceRaw] = useState("");
  const [salesPriceRaw, setSalesPriceRaw] = useState("");

  useEffect(() => {
    if (active && sourceItem) {
      setCurrentItem(sourceItem);
      setArticleNumber(sourceItem.product?.item_number || "");
      setPurchasePriceRaw("");
      setSalesPriceRaw("");
    }
  }, [active, sourceItem]);

  if (!sourceItem) return null;

  const initialValues = {
    description: currentItem.description || "",
    quantity: currentItem.quantity || 0,
    sales_price: currentItem.sales_price ? Number(currentItem.sales_price) : 0,
    purchase_price: currentItem.purchase_price ? Number(currentItem.purchase_price) : 0,
    discount: currentItem.discount ? Number(currentItem.discount) : 0,
    surcharge: currentItem.surcharge ? Number(currentItem.surcharge) : 0,
  };

  const validationSchema = Yup.object({
    quantity: Yup.number().min(0.01, "Antall må være større enn 0").required("Antall er påkrevd"),
    sales_price: Yup.number().min(0, "Pris kan ikke være negativ"),
    purchase_price: Yup.number().min(0, "Pris kan ikke være negativ"),
    discount: Yup.number().min(0, "Rabatt kan ikke være negativ"),
    surcharge: Yup.number().min(0, "Påslag kan ikke være negativ"),
  });

  const handleProductSelect = (products) => {
    if (!products || products.length === 0) {
      setProductSelectorOpen(false);
      return;
    }

    const selectedProduct = products[0];
    setCurrentItem({
      ...currentItem,
      product_id: selectedProduct.id,
      product: selectedProduct,
      unit_id: selectedProduct.unit?.id || selectedProduct.unit_id,
      unit: selectedProduct.unit,
      sales_price: selectedProduct.sales_price || currentItem.sales_price || 0,
      purchase_price: selectedProduct.purchase_price || currentItem.purchase_price || 0,
    });

    setArticleNumber(selectedProduct.item_number || "");
    setProductSelectorOpen(false);

    toast.success("Produkt valgt. Klikk «Lagre» for å oppdatere linjen.");
  };

  const handleArticleNumberSearch = async (num) => {
    if (!num || num.trim() === "") return;

    try {
      const response = await fetchProducts({
        query: num.trim(),
        page: 1,
        per_page: 10,
        fields: "id,title,displayTitle,item_number,unit,unit_id,sales_price,purchase_price",
      });

      if (response && response.data && response.data.length > 0) {
        const exactMatch = response.data.find(
          (p) =>
            p.item_number &&
            p.item_number.toLowerCase() === num.trim().toLowerCase()
        );

        if (exactMatch) {
          handleProductSelect([exactMatch]);
        } else {
          setProductSelectorOpen(true);
          toast.info("Ingen eksakt match funnet. Velg produkt fra listen.");
        }
      } else {
        toast.error("Ingen produkt funnet");
      }
    } catch (error) {
      console.error("Error searching product:", error);
      toast.error("Kunne ikke søke etter produkt");
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const updatedItem = {
        ...currentItem,
        description: values.description,
        quantity: values.quantity,
        sales_price: parseNumericValue(values.sales_price),
        purchase_price: parseNumericValue(values.purchase_price),
        discount: parseNumericValue(values.discount),
        surcharge: parseNumericValue(values.surcharge),
        product_id: currentItem.product_id || currentItem.product?.id,
        unit_id: currentItem.unit_id || currentItem.unit?.id,
        picked: false,
        picked_at: null,
        quantity_picked: null,
        taken_in_vehicle: false,
        returned_to_storage: false,
        left_in_vehicle: false,
        not_used: false,
        quantity_used: null,
        quantity_not_used: null,
      };

      const saved = await onSave(updatedItem);
      if (!skipSuccessToast) {
        toast.success("Linjedetaljer oppdatert. Produktet må markeres som plukket på nytt.");
      }
      if (saved && typeof saved === "object") {
        setCurrentItem(saved);
      }
    } catch (error) {
      console.error("Error updating item:", error);
      toast.error("Kunne ikke oppdatere linjedetaljer");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative">
      {productSelectorOpen && (
        <div className="absolute inset-0 z-[100] flex flex-col rounded-lg bg-white px-2 pb-4 pt-2 sm:px-0 sm:pb-0 sm:pt-0 min-h-[320px]">
          <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 px-2 pb-3 sm:px-0 sm:pb-4">
            <h3 className="text-lg font-medium text-gray-900">Velg produkt</h3>
            <button
              type="button"
              className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              onClick={() => setProductSelectorOpen(false)}
              aria-label="Lukk produktvelger"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-3 min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200 sm:mt-4">
            <div className="h-[min(500px,70vh)]">
              <ModernProductSelector
                open={true}
                selectedProducts={currentItem.product ? [currentItem.product] : []}
                onChange={handleProductSelect}
                onClose={() => setProductSelectorOpen(false)}
                multiple={false}
                filters={[]}
                showHeader={false}
              />
            </div>
          </div>
        </div>
      )}

      <h3 className="text-base font-semibold text-gray-900 mb-3">Produkt og pris</h3>
      <p className="text-sm text-gray-600 mb-4">
        Endring av antall eller produkt nullstiller plukk og varebruksstatus — du må registrere på nytt etter lagring.
      </p>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        key={`${currentItem.id}-${currentItem.product_id}-${currentItem.quantity}-${currentItem.sales_price}`}
      >
        {(formik) => (
          <Form>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Produkt</label>
                <div
                  className={`cursor-pointer hover:bg-blue-50 p-3 rounded border-2 border-dashed transition-colors ${
                    currentItem.product ? "border-blue-300 bg-blue-50" : "border-gray-300"
                  }`}
                  onClick={() => setProductSelectorOpen(true)}
                >
                  {currentItem.product ? (
                    <div>
                      <div className="font-bold text-blue-700">
                        {currentItem.product.displayTitle || currentItem.product.title}
                      </div>
                      {currentItem.product.item_number && (
                        <div className="text-xs text-gray-500 mt-1">
                          Art.nr: {currentItem.product.item_number}
                        </div>
                      )}
                      {currentItem.unit && (
                        <div className="text-xs text-gray-500 mt-1">
                          Enhet: {currentItem.unit.title} (følger produkt)
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-gray-500 text-center">
                      <div className="text-sm font-medium">+ Klikk for å velge produkt</div>
                    </div>
                  )}
                </div>
                <div className="flex space-x-2 mt-2">
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="Artikkelnr"
                    value={articleNumber}
                    onChange={(e) => setArticleNumber(e.target.value)}
                    onBlur={() => {
                      if (articleNumber) {
                        handleArticleNumberSearch(articleNumber);
                      }
                    }}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && articleNumber) {
                        handleArticleNumberSearch(articleNumber);
                      }
                    }}
                  />
                </div>
              </div>

              <PMField
                name="description"
                label="Beskrivelse"
                description={
                  !currentItem.product_id && !currentItem.product
                    ? "Beskrivelse for manuell vare"
                    : "Ekstra beskrivelse (valgfritt)"
                }
                multiline
                rows={3}
                {...formik}
              />

              <PMField
                name="quantity"
                type="number"
                label="Antall"
                step="0.01"
                min="0.01"
                {...formik}
              />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Innkjøpspris</label>
                  <TextField
                    fullWidth
                    variant="standard"
                    value={
                      purchasePriceRaw !== ""
                        ? purchasePriceRaw
                        : formik.values.purchase_price !== undefined &&
                            formik.values.purchase_price !== null &&
                            formik.values.purchase_price !== 0
                          ? formatPriceWithTwoDecimals(String(formik.values.purchase_price).replace(/\./g, ","))
                          : ""
                    }
                    onChange={(e) => {
                      const cleaned = e.target.value.replace(/[^0-9,]/g, "");
                      setPurchasePriceRaw(cleaned);
                    }}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      const value = purchasePriceRaw || e.target.value;
                      if (value === "" || value === null) {
                        formik.setFieldValue("purchase_price", 0);
                        setPurchasePriceRaw("");
                      } else {
                        const parsed = parseNumericValue(value);
                        formik.setFieldValue("purchase_price", parsed || 0);
                        setPurchasePriceRaw("");
                      }
                    }}
                    onFocus={(e) => {
                      if (purchasePriceRaw === "" && formik.values.purchase_price) {
                        setPurchasePriceRaw(String(formik.values.purchase_price).replace(/\./g, ","));
                      }
                      e.target.select();
                    }}
                    onKeyDown={(e) => {
                      const allowedKeys = /[0-9,.]/;
                      const isNavigationKey =
                        e.key === "Backspace" ||
                        e.key === "Delete" ||
                        e.key === "ArrowLeft" ||
                        e.key === "ArrowRight" ||
                        e.key === "Tab" ||
                        e.key === "Enter" ||
                        (e.key === "a" && e.ctrlKey) ||
                        (e.key === "A" && e.ctrlKey);

                      if (!allowedKeys.test(e.key) && !isNavigationKey) {
                        e.preventDefault();
                      }
                      if (e.key === ".") {
                        e.preventDefault();
                        const currentValue = purchasePriceRaw || e.target.value || "";
                        setPurchasePriceRaw(currentValue + ",");
                      }
                    }}
                    error={formik.touched.purchase_price && Boolean(formik.errors.purchase_price)}
                    helperText={formik.touched.purchase_price && formik.errors.purchase_price}
                    placeholder="0,00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Salgspris</label>
                  <TextField
                    fullWidth
                    variant="standard"
                    value={
                      salesPriceRaw !== ""
                        ? salesPriceRaw
                        : formik.values.sales_price !== undefined &&
                            formik.values.sales_price !== null &&
                            formik.values.sales_price !== 0
                          ? formatPriceWithTwoDecimals(String(formik.values.sales_price).replace(/\./g, ","))
                          : ""
                    }
                    onChange={(e) => {
                      const cleaned = e.target.value.replace(/[^0-9,]/g, "");
                      setSalesPriceRaw(cleaned);
                    }}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      const value = salesPriceRaw || e.target.value;
                      if (value === "" || value === null) {
                        formik.setFieldValue("sales_price", 0);
                        setSalesPriceRaw("");
                      } else {
                        const parsed = parseNumericValue(value);
                        formik.setFieldValue("sales_price", parsed || 0);
                        setSalesPriceRaw("");
                      }
                    }}
                    onFocus={(e) => {
                      if (salesPriceRaw === "" && formik.values.sales_price) {
                        setSalesPriceRaw(String(formik.values.sales_price).replace(/\./g, ","));
                      }
                      e.target.select();
                    }}
                    onKeyDown={(e) => {
                      const allowedKeys = /[0-9,.]/;
                      const isNavigationKey =
                        e.key === "Backspace" ||
                        e.key === "Delete" ||
                        e.key === "ArrowLeft" ||
                        e.key === "ArrowRight" ||
                        e.key === "Tab" ||
                        e.key === "Enter" ||
                        (e.key === "a" && e.ctrlKey) ||
                        (e.key === "A" && e.ctrlKey);

                      if (!allowedKeys.test(e.key) && !isNavigationKey) {
                        e.preventDefault();
                      }
                      if (e.key === ".") {
                        e.preventDefault();
                        const currentValue = salesPriceRaw || e.target.value || "";
                        setSalesPriceRaw(currentValue + ",");
                      }
                    }}
                    error={formik.touched.sales_price && Boolean(formik.errors.sales_price)}
                    helperText={formik.touched.sales_price && formik.errors.sales_price}
                    placeholder="0,00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <PMField name="discount" type="number" label="Rabatt (%)" step="0.01" min="0" {...formik} />
                <PMField name="surcharge" type="number" label="Påslag (%)" step="0.01" min="0" {...formik} />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                {showFooterCancel && (
                  <PMButton type="button" variant="secondary" onClick={onCancel} disabled={formik.isSubmitting}>
                    Avbryt
                  </PMButton>
                )}
                <PMButton type="submit" disabled={formik.isSubmitting} loading={formik.isSubmitting}>
                  {submitLabel}
                </PMButton>
              </div>
              {childrenAfterFooter}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
